/* eslint-disable max-lines-per-function */
import React, { useState, useContext, useEffect } from 'react';
import {
  Tooltip,
  Collapse,
  Typography,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MobileStepper from '@mui/material/MobileStepper';
import SwipeableViews from 'react-swipeable-views';
import { useTheme } from '@mui/material/styles';
import './employeeAchievementDetailsMobile.scss'; // Ensure this file has mobile-specific styles
import ViewAward from './EmployeeAwardModals/employeeViewAward';
import { SKILL_CERT_PROFILE } from '../../../Constants/strings';
import AddIcon from '@mui/icons-material/Add';
import { AuthContext } from '../../../Contexts/AuthContext';
import DeleteAward from './EmployeeAwardModals/employeeDeleteAward';
import NoEmployeeDetails from './noEmployeeDetails';
import AddAward from './EmployeeAwardModals/employeeAddAward';
import PropTypes from 'prop-types';
import * as employeeAchievementUtils from './employeeAchievementDetailsUtil';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import { useLocation } from 'react-router-dom';
import AwardCard from './awardCard';
import DriDetailCard from './driDetailCard';
import { MODULE_NAMES } from '../../../Constants/modules';
import SkillsAwardSection from './skillsAwardSection';

const SkillsCertAwardMobile = ({ userId }) => {
  const theme = useTheme();
  const [certificationsExpanded, setCertificationsExpanded] = useState(false);
  const [awardsExpanded, setAwardsExpanded] = useState(false);
  const [skillsExpanded, setSkillsExpanded] = useState(false);
  const [coreSkillsExpanded, setCoreSkillsExpanded] = useState(false);
  const [nonCoreSkillsExpanded, setNonCoreSkillsExpanded] = useState(false);
  const [driExpanded, setDriExpanded] = useState(false);
  const [activeStepCert, setActiveStepCert] = useState(0);
  const [activeStepAward, setActiveStepAward] = useState(0);
  const [activeStepDri, setActiveStepDri] = useState(0);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAward, setSelectedAward] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [skills, setSkills] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [awards, setAwards] = useState([]);
  const [dri, setDri] = useState([]);
  const { userProfile } = useContext(AuthContext);
  const { snackbarOpen } = useSnackbar();
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.EMPLOYEE_ACHIEVEMENTS] || []
    : [];
  const hasAccess = (action) => {
    return permission.includes(action);
  };
  const maxStepsCert = certifications?.totalMapsCount;
  const maxStepsAward = awards?.awardCount;
  const maxStepsDri = dri?.driDetails?.length;
  const axisDirection = theme.direction === 'rtl' ? 'x-reverse' : 'x';
  const location = useLocation();
  const { emailId } = location.state || {};

  useEffect(() => {
    fetchData();
  }, [userId]);
  const fetchData = async () => {
    try {
      const skillsData
        = await employeeAchievementUtils.fetchMappedSkills(userId); // Await the fetchMappedSkills function
      setSkills(skillsData);
      const certificationData
        = await employeeAchievementUtils.fetchMapCertifications(userId); // Await the fetchCertifications function
      setCertifications(certificationData);
      const awardData = await employeeAchievementUtils.fetchAwards(userId); // Await the fetchAwards function
      setAwards(awardData);
      const driData = await employeeAchievementUtils.fetchDri(emailId);
      setDri(driData);
    } catch (error) {
      snackbarOpen(SKILL_CERT_PROFILE.OOPS_TRY_AGAIN, 'warning');
      console.error('Error fetching data:', error);
    }
  };

  const toggleCertifications = () =>
    setCertificationsExpanded(!certificationsExpanded);
  const toggleSkills = () => setSkillsExpanded(!skillsExpanded);
  const toggleCoreSkills = () => setCoreSkillsExpanded(!coreSkillsExpanded);
  const toggleNonCoreSkills = () =>
    setNonCoreSkillsExpanded(!nonCoreSkillsExpanded);
  const toggleAwards = () => setAwardsExpanded(!awardsExpanded);
  const toggleDri = () => setDriExpanded(!driExpanded);

  const handleViewAward = (award) => () => {
    setViewModalOpen(true);
    setSelectedAward(award);
  };

  const handleDeleteAward = (award) => () => {
    setDeleteModalOpen(true);
    setSelectedAward(award);
  };

  return (
    <div className="skill-award-mobile-component">
      {/* Certifications */}
      <div className="mobile-section">
        <SkillsAwardSection
          sectionClass="skill-award-mobile-section"
          SKILL_CERT_PROFILE={SKILL_CERT_PROFILE}
          certifications={certifications}
          certificationsExpanded={certificationsExpanded}
          toggleCertifications={toggleCertifications}
        />
        <Collapse in={certificationsExpanded}>
          <div className="mobile-outer-div">
            {certifications?.certificationMapDetails?.length ? (
              <div className="cert-carousel-mobile-container">
                <div className="cert-carousel-mobile">
                  <SwipeableViews
                    axis={axisDirection}
                    index={activeStepCert}
                    onChangeIndex={setActiveStepCert}
                    enableMouseEvents
                  >
                    {certifications?.certificationMapDetails?.map((cert) => (
                      <div key={cert.id} className="cert-carousel-item">
                        <Card className="certification-card">
                          <CardContent>
                            <Typography className="profile-cert-id">
                              {cert.certification_code}
                              {cert.certification_type === 'Professional' && (
                                <span>{SKILL_CERT_PROFILE.PROFESSIONAL}</span>
                              )}
                            </Typography>
                            <Tooltip
                              title={cert.name}
                              classes={{ tooltip: 'map-role-btn-tooltip' }}
                              disableHoverListener={
                                cert.certifications.length < 30
                              }
                            >
                              <Tooltip>
                                <div className="profile-cert-name scooped-corners-mobile back">
                                  <Typography className="profile-cert-name-text scooped-corners">
                                    {cert.certifications.length > 30
                                      ? `${cert.certifications.substring(
                                        0,
                                        30,
                                      )}...`
                                      : cert.certifications}
                                  </Typography>
                                </div>
                              </Tooltip>
                            </Tooltip>
                            <Typography>
                              {SKILL_CERT_PROFILE.ACHIEVED_DATE}
                              &nbsp;&nbsp;&nbsp;
                              <span className="cert-date">
                                :
                                {employeeAchievementUtils.formatDate(
                                  cert.achievedDate,
                                )}
                              </span>
                            </Typography>
                            <Typography>
                              {SKILL_CERT_PROFILE.EXPIRY_DATE}
                              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                              <span className="cert-date">
                                :
                                {employeeAchievementUtils.formatDate(
                                  cert.expiredDate,
                                )}
                              </span>
                            </Typography>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </SwipeableViews>
                  <MobileStepper
                    className="profile-cert-stepper"
                    steps={maxStepsCert}
                    position="static"
                    activeStep={activeStepCert}
                  />
                </div>
              </div>
            ) : (
              <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA}/>
            )}
          </div>
        </Collapse>
      </div>

      {/* Skills */}
      <div className="mobile-section">
        <Typography className="skill-award-mobile-section">
          <span className="skills-award-title">
            {SKILL_CERT_PROFILE.SKILLS}
          </span>
          <span className="skills-award-count-text">
            {SKILL_CERT_PROFILE.TOTAL_SKILL_COUNT}:{' '}
            <span>{skills?.totalCount || 0}</span>
            <IconButton onClick={toggleSkills}>
              {skillsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </span>
        </Typography>
        <Collapse in={skillsExpanded}>
          <div className="mobile-outer-div">
            <div className="sub-mobile-section">
              <Typography className="skill-award-sub-mobile-section">
                <span>{SKILL_CERT_PROFILE.CORE}</span>
                <span className="skills-award-count-text">
                  {SKILL_CERT_PROFILE.TOTAL}:{' '}
                  <span>{skills?.coreSkillCount || 0}</span>
                  <IconButton onClick={toggleCoreSkills}>
                    {coreSkillsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </span>
              </Typography>
              <Collapse in={coreSkillsExpanded}>
                <div className="sub-mobile-section-outer-div scroll">
                  {skills?.skillMapDetails?.filter(
                    (skill) => skill.isCoreSkill === 1,
                  ).length > 0 ? (
                      <div className="skills-mobile-list">
                        {skills?.skillMapDetails
                          ?.filter((skill) => skill.isCoreSkill === 1)
                          .map((skill) => (
                            <div
                              key={skill?.skillMapId}
                              className="profile-skill-info"
                            >
                              <div
                                className={`skill-level ${
                                  skill?.skillLevel
                                    ? `level-${skill?.skillLevel}`
                                    : ''
                                }`}
                              >
                                {`L${skill?.skillLevel}`}
                              </div>
                              <Typography className="skill-name">
                                {skill?.skillName}
                              </Typography>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA}/>
                    )}
                </div>
              </Collapse>
            </div>
            <div className="sub-mobile-section">
              <Typography className="skill-award-sub-mobile-section">
                <span>{SKILL_CERT_PROFILE.NON_CORE}</span>
                <span className="skills-award-count-text">
                  {SKILL_CERT_PROFILE.TOTAL}:{' '}
                  <span>{skills?.nonCoreSkillCount || 0}</span>
                  <IconButton onClick={toggleNonCoreSkills}>
                    {nonCoreSkillsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </span>
              </Typography>
              <Collapse in={nonCoreSkillsExpanded}>
                <div className="sub-mobile-section-outer-div scroll">
                  {skills?.skillMapDetails?.filter(
                    (skill) => skill.isCoreSkill === 0,
                  ).length > 0 ? (
                      <div className="skills-mobile-list">
                        {skills?.skillMapDetails
                          ?.filter((skill) => skill.isCoreSkill === 0)
                          .map((skill) => (
                            <div
                              key={skill?.skillMapId}
                              className="profile-skill-info"
                            >
                              <div
                                className={`skill-level ${
                                  skill?.skillLevel
                                    ? `level-${skill?.skillLevel}`
                                    : ''
                                }`}
                              >
                                {`L${skill?.skillLevel}`}
                              </div>
                              <Typography className="skill-name">
                                {skill?.skillName}
                              </Typography>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA}/>
                    )}
                </div>
              </Collapse>
            </div>
          </div>
        </Collapse>
      </div>

      {/* Awards */}
      <div className="mobile-section">
        <Typography className="skill-award-mobile-section">
          <span className="skills-award-title">
            {SKILL_CERT_PROFILE.AWARDS}
            {hasAccess('create') && (
              <IconButton onClick={() => setAddModalOpen(true)}>
                <AddIcon />
              </IconButton>
            )}
          </span>
          <span className="skills-award-count-text">
            {SKILL_CERT_PROFILE.TOTAL_AWARD_COUNT}:{' '}
            <span>{awards?.awardCount || 0}</span>
            <IconButton onClick={toggleAwards}>
              {awardsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </span>
        </Typography>
        <Collapse in={awardsExpanded}>
          <div className="mobile-outer-div">
            {awards?.awards?.length ? (
              <div className="award-carousel-mobile-container">
                <div className="award-carousel-mobile">
                  <SwipeableViews
                    axis={axisDirection}
                    index={activeStepAward}
                    onChangeIndex={setActiveStepAward}
                    enableMouseEvents
                  >
                    {awards?.awards?.map((award) => (
                      <AwardCard
                        key={award.award_id}
                        award={award}
                        handleViewAward={handleViewAward}
                        handleDeleteAward={handleDeleteAward}
                        hasAccess={hasAccess} />
                    ))}
                  </SwipeableViews>
                  <MobileStepper
                    className="profile-award-stepper"
                    steps={maxStepsAward}
                    position="static"
                    activeStep={activeStepAward}
                  />
                </div>
              </div>
            ) : (
              <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA}/>
            )}
          </div>
        </Collapse>
      </div>

      {/* DRI Scores */}
      <div className="mobile-section">
        <Typography className="skill-award-mobile-section">
          <span className="skills-award-title">{SKILL_CERT_PROFILE.DRI}</span>
          <span className="skills-award-count-text">
            {SKILL_CERT_PROFILE.TOTAL_DRI_SCORE}:{' '}
            <span>{dri?.driDetails?.length || 0}</span>
            <IconButton onClick={toggleDri}>
              {driExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </span>
        </Typography>
        <Collapse in={driExpanded}>
          <div className="mobile-dri-outer-div">
            {!dri?.driDetails?.length > 0 ? (
              <NoEmployeeDetails message={SKILL_CERT_PROFILE.NO_DATA} noEmployeeCss="no-data-css" />
            ) : (
              <>
                <SwipeableViews
                  axis={axisDirection}
                  index={activeStepDri}
                  onChangeIndex={setActiveStepDri}
                  enableMouseEvents
                  className="mobile-dri-swipe"
                >
                  {dri.driDetails.map((score) => (
                    <DriDetailCard key={score.driId} score={score}/>
                  ))}
                </SwipeableViews>
                <MobileStepper
                  className="profile-award-stepper"
                  steps={maxStepsDri}
                  position="static"
                  activeStep={activeStepDri}
                />
              </>
            )}
          </div>
        </Collapse>
      </div>
      <ViewAward // viewAward popup
        modalOpen={viewModalOpen}
        handleClose={() => setViewModalOpen(false)}
        selectedAward={selectedAward}
      />
      <DeleteAward // deleteAward popup
        modalOpen={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        selectedAward={selectedAward}
        updatedData={fetchData}
        activeStepAward={activeStepAward}
        setActiveStepAward={setActiveStepAward}
      />
      <AddAward // AddAward popup
        modalOpen={addModalOpen}
        handleClose={() => setAddModalOpen(false)}
        userId={userId}
        updatedData={fetchData}
      />
    </div>
  );
};
SkillsCertAwardMobile.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default SkillsCertAwardMobile;
